import { useTranslation } from 'react-i18next';
import MoneyCell from '../MoneyCell';
import DateCell from '../DateCell';
import { useTableState, type ColumnDef } from '../../hooks/useTableState';
import type { FilterCondition } from '../../api/insights';

interface DrillTableProps<T extends Record<string, unknown>> {
  rows: T[] | undefined;
  columns: ColumnDef<T>[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; dir: 'asc' | 'desc' } | null;
  initialFilters?: FilterCondition[];
  pageSize?: number;
  emptyText?: string;
}

/**
 * Drill-down table: local search, sortable headers, declarative filter chips,
 * pagination — zero network calls. Rows are verbatim endpoint data; a row
 * click drills deeper (the visual-conversation "dig").
 */
export default function DrillTable<T extends Record<string, unknown>>({
  rows,
  columns,
  loading,
  onRowClick,
  initialSort,
  initialFilters,
  pageSize,
  emptyText,
}: DrillTableProps<T>) {
  const { t } = useTranslation();
  const table = useTableState(rows, columns, { initialSort, initialFilters, pageSize });

  const renderCell = (row: T, col: ColumnDef<T>) => {
    const value = col.value ? col.value(row) : row[col.key];
    switch (col.format) {
      case 'money':
        return <MoneyCell value={value as number | null | undefined} />;
      case 'date':
        return <DateCell value={value as string | null | undefined} />;
      default:
        return <>{value == null ? '—' : String(value)}</>;
    }
  };

  return (
    <div>
      {/* Controls: search + filter chips in one row above the table */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input
          type="search"
          value={table.search}
          onChange={e => table.setSearch(e.target.value)}
          placeholder={t('common.search')}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-md w-56 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        {table.filters.map((filter, i) => (
          <span
            key={`${filter.key}-${filter.op}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-50 text-primary-700 rounded-full"
          >
            {columns.find(c => c.key === filter.key)?.header ?? filter.key} {filter.op} {String(filter.value)}
            <button
              onClick={() => table.removeFilter(i)}
              className="text-primary-400 hover:text-primary-700"
              title={t('common.close')}
            >
              ×
            </button>
          </span>
        ))}
        {table.filters.length > 0 && (
          <button onClick={table.clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline">
            {t('analytics.table.clearFilters')}
          </button>
        )}
        <span className="ml-auto text-xs text-gray-400 tabular-nums">
          {table.total} {t('analytics.table.rows')}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`py-2 px-2 font-medium text-gray-500 select-none cursor-pointer hover:text-gray-800 ${
                    col.align === 'right' || col.format === 'money' || col.format === 'number' ? 'text-right' : ''
                  }`}
                  onClick={() => table.toggleSort(col.key)}
                >
                  {col.header}
                  {table.sort?.key === col.key && (
                    <span className="ml-1 text-primary-600">{table.sort.dir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="py-6 text-center text-gray-400">{t('analytics.table.loading')}</td>
              </tr>
            )}
            {!loading && table.paged.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="py-6 text-center text-gray-400">
                  {emptyText ?? t('analytics.table.empty')}
                </td>
              </tr>
            )}
            {!loading && table.paged.map((row, i) => (
              <tr
                key={i}
                className={`border-b border-gray-50 ${onRowClick ? 'cursor-pointer hover:bg-primary-50/50' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map(col => (
                  <td
                    key={col.key}
                    className={`py-2 px-2 ${col.align === 'right' || col.format === 'money' || col.format === 'number' ? 'text-right tabular-nums' : ''}`}
                  >
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {table.pageCount > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <button
            className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            disabled={table.page === 0}
            onClick={() => table.setPage(table.page - 1)}
          >
            ← {t('common.back')}
          </button>
          <span className="tabular-nums">{table.page + 1} / {table.pageCount}</span>
          <button
            className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            disabled={table.page >= table.pageCount - 1}
            onClick={() => table.setPage(table.page + 1)}
          >
            {t('analytics.table.next')} →
          </button>
        </div>
      )}
    </div>
  );
}
